/**
 * Input Component
 * 
 * Reusable input component with validation and error handling.
 * Follows University of Illinois design system.
 */

import React, { InputHTMLAttributes, forwardRef, CSSProperties } from 'react';
import './Input.css';

// Type definitions
type InputVariant = 'default' | 'filled' | 'outline';
type InputSize = 'sm' | 'md' | 'lg';

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  variant?: InputVariant;
  size?: InputSize;
  error?: string;
  helperText?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

/**
 * Input component
 */
const Input = forwardRef<HTMLInputElement, InputProps>(({
  variant = 'default',
  size = 'md',
  error,
  helperText,
  label,
  required = false,
  disabled = false,
  className = '',
  style = {},
  startIcon,
  endIcon,
  type = 'text',
  placeholder,
  ...props
}, ref) => {
  const baseClass = 'input';
  const variantClass = `input--${variant}`;
  const sizeClass = `input--${size}`;
  const errorClass = error ? 'input--error' : '';
  const disabledClass = disabled ? 'input--disabled' : '';
  const hasStartIconClass = startIcon ? 'input--has-start-icon' : '';
  const hasEndIconClass = endIcon ? 'input--has-end-icon' : '';
  
  const inputClasses = [
    baseClass,
    variantClass,
    sizeClass,
    errorClass,
    disabledClass,
    hasStartIconClass,
    hasEndIconClass,
    className
  ].filter(Boolean).join(' ');

  const inputId = props.id || `input-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className="input-wrapper" style={style}>
      {/* Label */}
      {label && (
        <label htmlFor={inputId} className="input__label">
          {label}
          {required && <span className="input__required" aria-label="required">*</span>}
        </label>
      )}
      
      {/* Input Container */}
      <div className="input__container">
        {/* Start Icon */}
        {startIcon && (
          <div className="input__start-icon" aria-hidden="true">
            {startIcon}
          </div>
        )}
        
        {/* Input Field */}
        <input
          ref={ref}
          id={inputId}
          type={type}
          className={inputClasses}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={
            error ? `${inputId}-error` : 
            helperText ? `${inputId}-helper` : 
            undefined
          }
          {...props}
        />
        
        {/* End Icon */}
        {endIcon && (
          <div className="input__end-icon" aria-hidden="true">
            {endIcon}
          </div>
        )}
      </div>
      
      {/* Error Message */}
      {error && (
        <div 
          id={`${inputId}-error`}
          className="input__error"
          role="alert"
          aria-live="polite"
        >
          {error}
        </div>
      )}
      
      {/* Helper Text */}
      {helperText && !error && (
        <div 
          id={`${inputId}-helper`}
          className="input__helper"
        >
          {helperText}
        </div>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
